import { Spinner } from '@kurtosys/ksys-portal-components/build/components/UI/Spinner.js';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import get from 'lodash/get';
import { observer, Provider } from 'mobx-react';
import * as React from 'react';
import { Component, Fragment } from 'react';
import MediaQuery from 'react-responsive';
import styled, { ThemeProvider } from 'styled-components';
import AppLoading from './Components/AppLoading';
import Disclaimers from './Components/Disclaimers';
import DocDone from './Components/DocDone';
import EmptyState from './Components/EmptyState';
import Filters from './Components/Filters';
import FiltersTop from './Components/FiltersTop';
import ResultsMeta from './Components/ResultsMeta';
import Fund from './Components/Fund';
import FundTypeTabs from './Components/FundTypeTabs';
import Header from './Components/Header';
import LoadingOverlay from './Components/LoadingOverlay';
import LoadMoreButton from './Components/LoadMoreButton';
import ModuleTabs from './Components/ModuleTabs';
import Translate from './Components/Translate';
import {
    DataStore,
    FilterStore,
    LoadingStore,
    DisclaimerStore,
    AccessibilityStore,
    AssetStore,
    TranslationStore,
} from './stores';
import { Stores } from './models/Stores';
import withInfiniteScroll from './Components/withInfiniteScroll';
import ModuleDropdown from './Components/ModuleDropdown';
import DocumentModule from './Components/DocumentModule';

interface AppProps {
    intlSupported?: boolean;
    culture?: string;
    fundlist?: string;
    module?: string;
    scriptSrc?: string;
    root?: HTMLElement;
}

const SearchHeader = styled<any, any>('header')`
    ${propStyles};
    top: 0;
`;

SearchHeader.defaultProps = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    padding: 'lg',
};

const Wrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

Wrapper.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    marginX: 'auto',
    fontFamily: 'body',
    fontSize: 'sm',
    color: 'body',
};

const Main = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

Main.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    margin: ['0', '0', '0 0 0 240px'],
};

const FundsWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FundsWrapper.defaultProps = {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
};

const Container = styled<any, any>('div')`
    ${propStyles};
`;

const InfiniteComponent = withInfiniteScroll(Wrapper);

@observer
class App extends Component<AppProps, any> {
    public stores: Stores;
    public dataStore: DataStore;
    public filterStore: FilterStore;
    public accessibilityStore: AccessibilityStore;
    public csvLink = React.createRef();
    constructor(props: AppProps) {
        super(props);
        const stores: Stores = {};
        stores.loading = new LoadingStore(stores);
        stores.disclaimer = new DisclaimerStore(stores);
        stores.data = new DataStore(stores, props);
        stores.translation = new TranslationStore(stores);
        stores.filter = new FilterStore(stores);
        stores.data.stores.filter = stores.filter;
        stores.accessibility = new AccessibilityStore(stores);
        stores.asset = new AssetStore(stores, stores.data);
        this.stores = stores;
        stores.data.init(props);
        // bind events
        this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
    }

    handleLoadMoreClick() {
        const { data } = this.stores;
        data.getFunds(true);
    }

    public render() {
        const {
            data,
            filter,
            loading,
            accessibility,
            // asset,
            translation,
        } = this.stores;

        return data.isBootstrapped ? (
            <Provider
                dataStore={data}
                filterStore={filter}
                loadingStore={loading}
                translationStore={translation}
                accessibilityStore={accessibility}
            >
                <ThemeProvider theme={() => data.theme}>
                    <Fragment>
                        {data.config.showFundTypes && <FundTypeTabs />}
                        <InfiniteComponent {...data.theme.Wrapper}>
                            {loading.global && (
                                <Fragment>
                                    <LoadingOverlay>
                                        <Spinner
                                            color="primary"
                                            iconSize="lg"
                                            {...data.theme.LoadingSpinner}
                                        />
                                    </LoadingOverlay>
                                    <DocDone />
                                </Fragment>
                            )}
                            {
                                <Fragment>
                                    {data.config.filtersTop ? (
                                        <MediaQuery
                                            query={`(max-width: ${data.theme.breakpoints.sm})`}
                                        >
                                            {matches =>
                                                matches && (
                                                    <Filters
                                                        isMobile={matches}
                                                    />
                                                )
                                            }
                                        </MediaQuery>
                                    ) : (
                                        <MediaQuery
                                            query={`(max-width: ${data.theme.breakpoints.md})`}
                                        >
                                            {matches => (
                                                <Filters isMobile={matches} />
                                            )}
                                        </MediaQuery>
                                    )}
                                </Fragment>
                            }
                            <MediaQuery
                                query={`(max-width: ${data.theme.breakpoints.sm})`}
                            >
                                {matches => {
                                    if (matches) {
                                        data.setBreakpointSize('sm');
                                    }
                                    data.setIsMobile(matches);
                                    return null;
                                }}
                            </MediaQuery>
                            <MediaQuery
                                query={`(max-width: ${data.theme.breakpoints.md})`}
                            >
                                {matches => {
                                    if (matches) {
                                        data.setBreakpointSize('md');
                                    }
                                    data.setIsMobile(matches);
                                    return null;
                                }}
                            </MediaQuery>
                            <MediaQuery
                                query={`(min-width: ${data.theme.breakpoints.md})`}
                            >
                                {matches => {
                                    if (matches) {
                                        data.setBreakpointSize('lg');
                                    }
                                    return null;
                                }}
                            </MediaQuery>
                            <Main
                                {...data.theme.Main}
                                margin={
                                    data.config.filtersTop ||
                                    get(data, 'theme.Main.margin', [
                                        '0',
                                        '0',
                                        '0 0 0 240px',
                                    ])
                                }
                            >
                                <div
                                    id={'fundcenter-context-menu'}
                                    style={{
                                        position: 'relative',
                                        zIndex: 1010,
                                    }}
                                />
                                <Container {...data.theme.HeaderWrapper}>
                                    <Container {...data.theme.HeaderContent}>
                                        <Header />
                                        {data.config.filtersTop && (
                                            <MediaQuery
                                                query={`(min-width: ${data.theme.breakpoints.sm})`}
                                            >
                                                {matches =>
                                                    matches && (
                                                        <FiltersTop
                                                            isMobile={matches}
                                                        />
                                                    )
                                                }
                                            </MediaQuery>
                                        )}
                                        {data.config.showModules && (
                                            <MediaQuery
                                                query={`(min-width: ${data.theme.breakpoints.sm})`}
                                            >
                                                {matches =>
                                                    !matches &&
                                                    data.config
                                                        .moduleMobileSelector ===
                                                        'dropdown' ? (
                                                        <ModuleDropdown />
                                                    ) : (
                                                        <ModuleTabs />
                                                    )
                                                }
                                            </MediaQuery>
                                        )}
                                    </Container>
                                </Container>
                                <Container {...data.theme.ContentWrapper}>
                                    <Container {...data.theme.Content}>
                                        <Disclaimers
                                            disclaimers={data.headerDisclaimers}
                                            theme={{
                                                ...data.theme,
                                                DisclaimersList: {
                                                    ...data.theme
                                                        .DisclaimersList,
                                                    ...data.theme
                                                        .HeaderDisclaimersList,
                                                },
                                                DisclaimerListItem: {
                                                    ...data.theme
                                                        .DisclaimerListItem,
                                                    ...data.theme
                                                        .HeaderDisclaimerListItem,
                                                },
                                                DisclaimerContent: {
                                                    ...data.theme
                                                        .DisclaimerContent,
                                                    ...data.theme
                                                        .HeaderDisclaimerContent,
                                                },
                                            }}
                                        />
                                        {data.config.isDocumentModule ? (
                                            <DocumentModule />
                                        ) : (
                                            <FundsWrapper>
                                                {data.groupedFunds.length >
                                                0 ? (
                                                    <Fragment>
                                                        <ResultsMeta
                                                            shareClasses={
                                                                data
                                                                    .groupedFunds[0]
                                                                    .shareClasses
                                                            }
                                                        />
                                                        {data.groupedFunds.map(
                                                            (fund, index) => (
                                                                <Fund
                                                                    key={`${fund.fundName}-${index}`}
                                                                    grouped={
                                                                        fund.grouped
                                                                    }
                                                                    fundName={
                                                                        fund.fundName
                                                                    }
                                                                    shareClasses={
                                                                        fund.shareClasses
                                                                    }
                                                                    columns={
                                                                        fund.columns
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                        <LoadMoreButton
                                                            onClick={
                                                                this
                                                                    .handleLoadMoreClick
                                                            }
                                                            disabled={
                                                                data.hasAllFunds
                                                            }
                                                            show={
                                                                !data.hideLoadMoreButton
                                                            }
                                                            isLoading={
                                                                loading.fundsMore
                                                            }
                                                        />
                                                    </Fragment>
                                                ) : (
                                                    <Fragment>
                                                        {!loading.global && (
                                                            <EmptyState>
                                                                <Translate>
                                                                    {
                                                                        data
                                                                            .config
                                                                            .noDataText
                                                                    }
                                                                </Translate>
                                                            </EmptyState>
                                                        )}
                                                    </Fragment>
                                                )}
                                            </FundsWrapper>
                                        )}

                                        <Disclaimers
                                            disclaimers={data.footerDisclaimers}
                                            theme={{
                                                ...data.theme,
                                                DisclaimersList: {
                                                    ...data.theme
                                                        .DisclaimersList,
                                                    ...data.theme
                                                        .FooterDisclaimersList,
                                                },
                                                DisclaimerListItem: {
                                                    ...data.theme
                                                        .DisclaimerListItem,
                                                    ...data.theme
                                                        .FooterDisclaimerListItem,
                                                },
                                                DisclaimerContent: {
                                                    ...data.theme
                                                        .DisclaimerContent,
                                                    ...data.theme
                                                        .FooterDisclaimerContent,
                                                },
                                            }}
                                        />
                                    </Container>
                                </Container>
                            </Main>
                        </InfiniteComponent>
                    </Fragment>
                </ThemeProvider>
            </Provider>
        ) : (
            <AppLoading />
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx